import { Component, OnInit, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UtilService, DialogService, PagedResult, NotificationService } from '../../../shared/index';
import { EventRead } from '../../shared/event-read.model';
import { EventCustomRegField } from '../../shared/custom-reg-fields/event-custom-reg-field.model';
import { EventCustomRegFieldAvailValues } from '../../shared/custom-reg-fields/event-custom-reg-field-avail-values.model';
import { EventV4ItemService } from '../../shared/items/event-v4-item.service';
import { EventCustomRegFieldUiControlType } from '../../shared/custom-reg-fields/event-custom-reg-field-ui-control-type.model';

import { AsyncState } from '../../../shared/model/index';
import { UserService } from '../../../users/shared/user.service';
import { TranslationService } from '../../../shared/translation/translation.service';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { EventV4CustomRegFieldService } from '../../shared/custom-reg-fields/event-v4-custom-reg-field.service';

@Component({
  styleUrls: ['event-v4-settings-registration-fields.component.css'],
  selector: 'wl-event-v4-settings-registration-fields',
  templateUrl: 'event-v4-settings-registration-fields.component.html',
  providers: [EventV4CustomRegFieldService, UserService],
    standalone: false
})
export class EventV4SettingsRegistrationFieldsComponent implements OnInit {
  public loadingCustomRegFields: AsyncState = new AsyncState();
  public loadingCustomRegField: AsyncState = new AsyncState();
  public get totalAdditionalCost(): number {
    if (!this.eventCustomRegField || !this.eventCustomRegField.AvailableValues || !this.eventCustomRegField.AvailableValues.length)
      return 0;

    return this.eventCustomRegField.AvailableValues.map((x) => (x ? x.Amount : 0)).reduce((total, x) => total + x);
  }

  public uiControlTypes: Array<any>;

  public eventCustomRegField: EventCustomRegField;

  public eventCustomRegFieldId: number;
  public eventId: number;
  public customRegFields: Array<EventCustomRegField>;
  public eventItems: Array<any>;

  public _event: EventRead;
  @Input() public get event(): EventRead {
    return this._event;
  }
  public set event(value: EventRead) {
    if (this._utilService && this._utilService.isNotNullOrUndefined(value)) {
      this._event = value;
    }
  }

  public customFieldSaving: boolean = false;
  public customFieldSaveError: string = '';

  public userAccessLevel: number;
  public showModal = false;

  public constructor(
    private _route: ActivatedRoute,
    private _eventCustomRegFieldService: EventV4CustomRegFieldService,
    private _dialogService: DialogService,
    private _utilService: UtilService,
    private _eventItemService: EventV4ItemService,
    private _userService: UserService,
    public _notificationService: NotificationService,
    public _translationService: TranslationService
  ) {
    this._event = new EventRead();
    this.eventCustomRegField = new EventCustomRegField();
    this.customRegFields = new Array<EventCustomRegField>();
    this.eventItems = new Array<any>();

    this.userAccessLevel = 0;

    this.uiControlTypes = Object.keys(EventCustomRegFieldUiControlType)
      .filter((x) => {
        return isNaN(Number(x));
      })
      .map((x) => {
        return {
          value: x,
          display: 'enum.eventcustomregfielduicontroltype.' + x.toLowerCase(),
        };
      });
  }

  ngOnInit() {
    this._route.params.subscribe((params) => {
      if (this._utilService.isNumeric(params['id'])) {
        let id = Number(params['id']);
        this.eventId = id;

        this.loadCustomRegFields();
      }
    });

    this._userService.getCurrentUserSecurityElementAccessLevel('Events_Module').then((data: number) => {
      this.userAccessLevel = data;
    });
  }

  public newCustomRegField = (): void => {
    this.customFieldSaving = false;
    this.customFieldSaveError = '';

    this.loadingCustomRegField.busy = true;
    this.loadingCustomRegField.message = '';
    this.loadingCustomRegField.success = false;

    let newCustomRegField: EventCustomRegField = new EventCustomRegField();
    newCustomRegField.EventId = this.eventId;
    this.eventCustomRegField = newCustomRegField;

    this.showModal = true;

    this._eventItemService
      .getEventItems(this.eventId)
      .then((data) => {
        this.eventCustomRegField.EventItemIds = data.map((x) => x.EventItemId);

        this.eventItems = data.map<any>((item) => {
          return {
            eventItemName: item.Descr,
            apply:
              this.eventCustomRegField.EventItemIds.some((x) => x == item.EventItemId) ||
              item.EventItemId == this.eventCustomRegField.EventItemId,
            eventItemId: item.EventItemId,
          };
        });

        this.loadingCustomRegField.busy = false;
        this.loadingCustomRegField.success = true;
      })
      .catch((error) => {
        this.loadingCustomRegField.busy = false;
        this.loadingCustomRegField.message = this._utilService.extractErrorMessage(error);
        this.loadingCustomRegField.success = false;
      });
  };

  public loadCustomRegFields = (forceRefresh?: boolean): void => {
    this.loadingCustomRegFields.busy = true;
    this.loadingCustomRegFields.message = '';
    this.loadingCustomRegFields.success = false;

    this._eventCustomRegFieldService
      .getEventCustomRegFields(this.eventId, forceRefresh)
      .then((data: PagedResult) => {
        this.loadingCustomRegFields.busy = false;
        this.loadingCustomRegFields.success = true;

        this.customRegFields = data && data.Result ? <Array<EventCustomRegField>>data.Result : [];

        if (this.customRegFields.length > 0) {
          this.customRegFields = this.customRegFields.sort((a, b) => a.DisplayOrder - b.DisplayOrder);
          this.eventCustomRegField = this.customRegFields[0];
        }
      })
      .catch((error) => {
        this.loadingCustomRegFields.busy = false;
        this.loadingCustomRegFields.message = this._utilService.extractErrorMessage(error);
        this.loadingCustomRegFields.success = false;
      });
  };

  public saveCustomRegFields = (): void => {
    this.customFieldSaving = true;
    this.customFieldSaveError = '';

    const dropDownHasNoAvailableValuesAdded =
      this.eventCustomRegField.AvailableValues?.length === 0 && this.eventCustomRegField.UiControlType === 'DropdownList';
    if (dropDownHasNoAvailableValuesAdded) {
      this._translationService
        .get('events.settings.registration-fields.available-values-placeholder')
        .then((translation) => (this.customFieldSaveError = translation));

      this.customFieldSaving = false;
      return;
    }

    const thereAreAvailableValuesWithEmptyValueOrDescr = this.eventCustomRegField.AvailableValues?.some(
      (v) => !v.Value || v.Value.trim() === '' || !v.Descr || v.Descr.trim() === ''
    );
    if (thereAreAvailableValuesWithEmptyValueOrDescr) {
      this._translationService
        .get('events.settings.registration-fields.available-values-or-description-not-filled')
        .then((translation) => (this.customFieldSaveError = translation));

      this.customFieldSaving = false;
      return;
    }

    this.eventCustomRegField.EventItemIds = this.eventItems.filter((x) => x.apply).map((x) => x.eventItemId);

    if (
      this.eventCustomRegField.EventItemId &&
      !this.eventCustomRegField.EventItemIds.some((x) => x == this.eventCustomRegField.EventItemId)
    )
      this.eventCustomRegField.EventItemId = 0;

    this._eventCustomRegFieldService
      .upsertEventCustomRegField(this.eventCustomRegField)
      .then(() => {
        this.customFieldSaving = false;
        this.loadCustomRegFields(true);
        this.showModal = false;

        this._translationService
          .getTitlecase('events.settings.registration-fields.custom-field-save-success-message')
          .then((translation) => this._notificationService.success(translation));
      })
      .catch((error) => {
        this.customFieldSaving = false;
        this.customFieldSaveError = this._utilService.extractErrorMessage(error);
      });
  };

  public editCustomRegField = (eventCustomRegFieldId: number): void => {
    this.customFieldSaving = false;
    this.customFieldSaveError = '';

    this.loadingCustomRegField.busy = true;
    this.loadingCustomRegField.message = '';
    this.loadingCustomRegField.success = false;

    this.showModal = true;

    // load the custom field
    this._eventCustomRegFieldService
      .getEventCustomRegField(eventCustomRegFieldId)
      .then((customField) => {
        this.loadingCustomRegField.success = true;

        this.eventCustomRegField = customField;

        this._eventItemService.getEventItems(this.eventId).then((data) => {
          this.eventItems = data.map<any>((item) => {
            return {
              eventItemName: item.Descr,
              apply:
                this.eventCustomRegField.EventItemIds.some((x) => x == item.EventItemId) ||
                item.EventItemId == this.eventCustomRegField.EventItemId,
              eventItemId: item.EventItemId,
            };
          });
          this.loadingCustomRegField.busy = false;
        });
      })
      .catch((error) => {
        this.loadingCustomRegField.busy = false;
        this.loadingCustomRegField.message = this._utilService.extractErrorMessage(error);
        this.loadingCustomRegField.success = false;
      });
  };

  public confirmDeleteCustomRegField = (eventCustomRegFieldId: number, regFieldIndex: number): void => {
    this._dialogService.confirmYesNo(
      'Are you sure you want to delete this custom field?',
      () => {
        this.deleteCustomRegField(eventCustomRegFieldId, regFieldIndex);
      },
      () => {}
    );
  };

  public deleteCustomRegField = (eventCustomRegFieldId: number, regFieldIndex: number): void => {
    this._dialogService.showWaitIndicator();
    this.customRegFields.splice(regFieldIndex, 1);
    this._eventCustomRegFieldService
      .deleteEventCustomRegField(eventCustomRegFieldId)
      .then(() => {
        this.loadCustomRegFields(true);
        this._dialogService.closeWaitIndicator();
      })
      .catch(() => {
        this._dialogService.closeWaitIndicator();
      });
  };

  public addAvailableValue = (): void => {
    var customRegFieldValue = new EventCustomRegFieldAvailValues();
    customRegFieldValue.EventCustomRegFieldId = this.eventCustomRegField.Id;
    this.eventCustomRegField.AvailableValues.push(customRegFieldValue);
  };

  public removeAvailableValue = (index: number): void => {
    this.eventCustomRegField.AvailableValues.splice(index, 1);
  };

  public customFieldHasEventItem = (dataItem: EventCustomRegField, eventItemId: number) => {
    return !(dataItem.EventItemIds.some((x) => x == eventItemId) || dataItem.EventItemId == eventItemId);
  };

  public bitWAnd = (opperand1: number, opperand2: number): boolean => {
    return (opperand1 & opperand2) > 0;
  };

  /**
   * respond to the custom field grid being sorted
   */
  public handleCustomFieldSortDrop(event): void {
    moveItemInArray(this.customRegFields, event.previousIndex, event.currentIndex);
    let customFieldsNewOrder: Array<number> = this.customRegFields.map((x) => x.Id);

    this._eventCustomRegFieldService.reorderCustomFields(this.eventId, customFieldsNewOrder);
  }

  /**
   * move an available value up the array by one
   */
  public handleAvailableValueMoveUp(currentIndex: number): void {
    if (
      this.eventCustomRegField.AvailableValues == null ||
      !this.eventCustomRegField.AvailableValues.length ||
      currentIndex < 1 ||
      currentIndex >= this.eventCustomRegField.AvailableValues.length
    )
      return;

    var values = this.eventCustomRegField.AvailableValues.splice(currentIndex - 1, 2);

    this.eventCustomRegField.AvailableValues.splice(currentIndex - 1, 0, values[1], values[0]);

    // add a highlight, so the user can track the moved item
    (<any>values[1])._highlight = true;
    setTimeout(() => ((<any>values[1])._highlight = false), 500);
  }

  /**
   * move an available value down the array by one
   */
  public handleAvailableValueMoveDown(currentIndex: number): void {
    if (
      this.eventCustomRegField.AvailableValues == null ||
      !this.eventCustomRegField.AvailableValues.length ||
      currentIndex < 0 ||
      currentIndex > this.eventCustomRegField.AvailableValues.length - 2
    )
      return;

    var values = this.eventCustomRegField.AvailableValues.splice(currentIndex, 2);

    this.eventCustomRegField.AvailableValues.splice(currentIndex, 0, values[1], values[0]);

    // add a highlight, so the user can track the moved item
    (<any>values[0])._highlight = true;
    setTimeout(() => ((<any>values[0])._highlight = false), 600);
  }
}
